import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MutationStatus } from 'react-query';
import { joiResolver } from '@hookform/resolvers/joi';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import ApiButton from 'web-library/stories/atom/Button/ApiButton';
import Button from 'web-library/stories/atom/Button/Button';
import Grid from 'web-library/stories/atom/Grid/Grid';
import { useHistory } from 'react-router-dom';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../models';
import schemaCreateCandidate from '../../schema/schemaCreateCandidate';
import ProfileDetails from './ProfileDetails';
import { useGetSingPassConsent } from '../../../../api-refactored/user/useGetSingPassConsent';
import RouteKey from '../../../../routes/key';

const ErrorInfo = lazy(() => import('web-library/stories/molecule/ErrorInfo/ErrorInfo'));
const OptionalSingpassLoginModal = lazy(() => import('../OptionalSingpassLoginModal'));
const MandatorySingpassLoginModal = lazy(() => import('../MandatorySingpassLoginModal'));
const PersonalParticulars = lazy(() => import('./PersonalParticulars'));

const ContactInformation = lazy(() => import('./ContactInformation'));
const NextOfKinDetails = lazy(() => import('./NextOfKinDetails'));

const useStyles = makeStyles()((theme) => ({
  actionButton: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(3),
    },
  },
  errorMsg: {
    marginTop: 0,
    paddingBottom: theme.spacing(2),
  },
}));

interface CandidateProfileFormProps {
  onCallVerifyCandidate?: (formData: CandidateProfileFormData) => void;
  onCallHandleSubmit?: (formData: CandidateProfileFormData) => void;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isDisableValidationForDisabledInputs?: boolean;
  verifyCandidateSubmitError?: string;
  isSingPassUser?: boolean;
  profileData: CandidateProfileFormData | undefined;
  setProfileData: (formData: CandidateProfileFormData) => void;
  verifyCandidateStatus: MutationStatus;
  createAccountStatus: MutationStatus;
  mandatorySingpass: boolean;
  optionalSingpass: boolean;
  createCandidateError: string | undefined;
}

const { serverBusy } = RouteKey.error.child;
const { error: errorRoute } = RouteKey.child;
const redirectIfServerBusy = (errorCode: string, history: any) => {
  if (errorCode === '1020') {
    history.push(`/${errorRoute}/${serverBusy}`);
  }
};

const isDisableAfterVerifyManual = {
  hanyupinyinName: false,
  gender: false,
  race: false,
  citizenship: true,
  nationality: false,
  nationalityIssueDate: false,
  prStatus: false,
  occupation: false,
  email: false,
  reEnterEmail: false,
  mobileCountryCode: false,
  mobileContactNumber: false,
  preferredTelephoneOrMobile: false,
  telephoneCountryCode: false,
  telephoneContactNumber: false,
  houseOrBlkNo: false,
  streetName: false,
  building: false,
  floor: false,
  unit: false,
  postalCode: false,
  sameAsRegisteredAddress: false,
  preferredAddress: false,
  foreignAddress1: false,
  foreignAddress2: false,
  foreignAddress3: false,
  nextOfKinName: false,
  nextOfKinRelationshipToCandidate: false,
  nextOfKinCountryCode: false,
  nextOfKinContactNumber: false,
};

const CandidateProfileForm = ({
  onCallVerifyCandidate = () => {
    // do nothing
  },
  onCallHandleSubmit = () => {
    // do nothing
  },
  formDefaultValues = undefined,
  isDisableInputs = undefined,
  isDisableValidationForDisabledInputs = false,
  verifyCandidateSubmitError = '',
  isSingPassUser = false,
  profileData,
  setProfileData,
  verifyCandidateStatus,
  createAccountStatus,
  mandatorySingpass,
  optionalSingpass,
  createCandidateError,
}: CandidateProfileFormProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const [verifyButtonStatus, setVerifyButtonStatus] = useState<MutationStatus>('idle');
  const checkVerifyButtonStatus = (index: MutationStatus) => {
    setVerifyButtonStatus(index);
  };
  const [singpassError, setSingpassError] = useState<string | null>(null);
  const isCreateLoading = createAccountStatus === 'loading';
  const canSubmitAfterVerify = verifyButtonStatus === 'success';

  // disable if not verified OR creating
  const isSubmitDisabled = !canSubmitAfterVerify || isCreateLoading;
  const { refetch: singPassConsentRefetch } = useGetSingPassConsent({
    onSuccess: (res) => {
      window.location.href = res.url;
    },
    onError: (res) => {
      redirectIfServerBusy(res.response?.data?.code?.toString()!, history);
      setSingpassError('Something wrong with SingPass.');
    },
  });

  const isDisableAfterVerifySingpass = {
    hanyupinyinName: isDisableInputs?.hanyupinyinName,
    gender: isDisableInputs?.gender,
    race: isDisableInputs?.race,
    citizenship: isDisableInputs?.citizenship,
    nationality: isDisableInputs?.nationality,
    nationalityIssueDate: false,
    prStatus: isDisableInputs?.prStatus,
    occupation: false,
    email: false,
    reEnterEmail: false,
    mobileCountryCode: false,
    mobileContactNumber: false,
    preferredTelephoneOrMobile: false,
    telephoneCountryCode: false,
    telephoneContactNumber: false,
    houseOrBlkNo: false,
    streetName: false,
    building: false,
    floor: false,
    unit: false,
    postalCode: false,
    sameAsRegisteredAddress: false,
    preferredAddress: false,
    foreignAddress1: false,
    foreignAddress2: false,
    foreignAddress3: false,
    nextOfKinName: false,
    nextOfKinRelationshipToCandidate: false,
    nextOfKinCountryCode: false,
    nextOfKinContactNumber: false,
  };

  const isDisableBeforeVerify = {
    ...isDisableInputs,
    hanyupinyinName: true,
    prStatus: true,
  };
  const [isOptionalSingpassLoginModalOpen, setIsOptionalSingpassLoginModalOpen] = useState(true);
  const isDisableAfterVerify = isSingPassUser
    ? isDisableAfterVerifySingpass
    : isDisableAfterVerifyManual;

  const disableFields =
    verifyButtonStatus === 'success' ? isDisableAfterVerify : isDisableBeforeVerify;

  let verifyStatus = verifyCandidateStatus;
  if (mandatorySingpass && !isSingPassUser) {
    verifyStatus = 'error';
  }

  const formHook = useForm<CandidateProfileFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(
      schemaCreateCandidate(isDisableValidationForDisabledInputs ? disableFields : {})
    ),
    defaultValues: formDefaultValues,
  });

  const { handleSubmit } = formHook;

  useEffect(() => {
    if (formDefaultValues) {
      formHook.reset(formDefaultValues);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [formDefaultValues]);

  useEffect(() => {
    if (verifyButtonStatus === 'success') {
      setDisableButton(false);
    }
  }, [verifyButtonStatus]);

  return (
    <>
      <ProfileDetails
        onCallVerifyCandidate={onCallVerifyCandidate}
        isDisableInputs={isDisableInputs}
        isDisableValidationForDisabledInputs
        formDefaultValues={formDefaultValues}
        verifyCandidateSubmitError={verifyCandidateSubmitError}
        checkVerifyButtonStatus={checkVerifyButtonStatus}
        setProfileData={setProfileData}
        verifyCandidateStatus={verifyStatus}
        errorTime={mandatorySingpass ? 1000000 : 1500}
        isSingPassUser={isSingPassUser}
      />
      <form
        onSubmit={handleSubmit((formData) => {
          onCallHandleSubmit(formData);
        })}
      >
        <Suspense fallback={null}>
          <PersonalParticulars
            formHook={formHook}
            isDisableInputs={disableFields}
            formDefaultValues={formDefaultValues}
            profileData={profileData}
            isSingPassUser={isSingPassUser}
            isIExamsApiEnabled={!!profileData}
          />
          <ContactInformation
            formHook={formHook}
            isDisableInputs={disableFields}
            formDefaultValues={formDefaultValues}
            isSingPassUser={isSingPassUser}
            isIExamsApiEnabled={!!profileData}
          />
          <NextOfKinDetails
            formHook={formHook}
            isDisableInputs={disableFields}
            formDefaultValues={formDefaultValues}
            isIExamsApiEnabled={!!profileData}
          />
        </Suspense>
        {createCandidateError && (
          <Suspense fallback={null}>
            <ErrorInfo label={createCandidateError} className={classes.errorMsg} />
          </Suspense>
        )}
        <Grid container direction="row" justify="flex-start">
          <Button
            variant="outlined"
            className={classes.actionButton}
            onClick={() => {
              history.push('/');
            }}
          >
            {t('profile->>CreateCandidateForm.Cancel')}
          </Button>
          <ApiButton
            id="button-candidateProfile-submit"
            status={createAccountStatus}
            disabled={isSubmitDisabled}
            type="submit"
            className={classes.actionButton}
          >
            {t('profile->>CreateCandidateForm.Create Account')}
          </ApiButton>
        </Grid>
      </form>
      {optionalSingpass && isOptionalSingpassLoginModalOpen && !isSingPassUser && (
        <Suspense fallback={null}>
          <OptionalSingpassLoginModal
            isOpen={optionalSingpass && isOptionalSingpassLoginModalOpen && !isSingPassUser}
            onHavingSingpass={() => singPassConsentRefetch()}
            onNotHavingSingpass={() => {
              setIsOptionalSingpassLoginModalOpen(false);
            }}
            error={singpassError}
          />
        </Suspense>
      )}
      {mandatorySingpass && !isSingPassUser && (
        <Suspense fallback={null}>
          <MandatorySingpassLoginModal
            isOpen={mandatorySingpass && !isSingPassUser}
            onHavingSingpass={() => singPassConsentRefetch()}
            error={singpassError}
          />
        </Suspense>
      )}
    </>
  );
};
export default CandidateProfileForm;
